import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import OwnerIntegrations from './OwnerIntegrations';

const API = process.env.REACT_APP_API_URL || '';

function getToken() { return localStorage.getItem('hrtrack_admin_token'); }

const PLANS = ['trial', 'starter', 'growth', 'business'];
const STATUSES = ['trial', 'active', 'cancelled', 'past_due'];

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: '20px 24px', flex: 1, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
      <div style={{ fontSize: '2rem', fontWeight: 800, color: color || 'var(--text)' }}>{value}</div>
      <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function EditModal({ ws, onSave, onClose }) {
  const [form, setForm] = useState({
    plan: ws.plan || 'trial',
    subscription_status: ws.subscription_status || 'trial',
    seats_limit: ws.seats_limit ?? '',
    trial_ends_at: ws.trial_ends_at ? ws.trial_ends_at.substring(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(ws.id, {
        plan: form.plan,
        subscription_status: form.subscription_status,
        seats_limit: form.seats_limit === '' ? null : parseInt(form.seats_limit),
        trial_ends_at: form.trial_ends_at || null,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  // Quick presets
  const applyPreset = (preset) => {
    if (preset === 'business') setForm(f => ({ ...f, plan: 'business', subscription_status: 'active', seats_limit: '', trial_ends_at: '' }));
    if (preset === 'extend7') {
      const d = new Date(); d.setDate(d.getDate() + 7);
      setForm(f => ({ ...f, trial_ends_at: d.toISOString().substring(0, 10), subscription_status: 'trial' }));
    }
    if (preset === 'extend30') {
      const d = new Date(); d.setDate(d.getDate() + 30);
      setForm(f => ({ ...f, trial_ends_at: d.toISOString().substring(0, 10), subscription_status: 'trial' }));
    }
    if (preset === 'cancel') setForm(f => ({ ...f, subscription_status: 'cancelled' }));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>{ws.name}</div>
        <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginBottom: 20 }}>{ws.owner_email}</div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          <button onClick={() => applyPreset('business')} style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #4f46e5', color: '#4f46e5', background: 'none', cursor: 'pointer' }}>⚡ Set Business (Permanent)</button>
          <button onClick={() => applyPreset('extend7')} style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #0891b2', color: '#0891b2', background: 'none', cursor: 'pointer' }}>+7 days trial</button>
          <button onClick={() => applyPreset('extend30')} style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #0891b2', color: '#0891b2', background: 'none', cursor: 'pointer' }}>+30 days trial</button>
          <button onClick={() => applyPreset('cancel')} style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #ef4444', color: '#ef4444', background: 'none', cursor: 'pointer' }}>Cancel</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Plan</label>
            <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem' }}>
              {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Status</label>
            <select value={form.subscription_status} onChange={e => setForm(f => ({ ...f, subscription_status: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem' }}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Seats Limit (blank = unlimited)</label>
            <input type="number" value={form.seats_limit} onChange={e => setForm(f => ({ ...f, seats_limit: e.target.value }))} placeholder="Unlimited" style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Trial Ends At</label>
            <input type="date" value={form.trial_ends_at} onChange={e => setForm(f => ({ ...f, trial_ends_at: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem', boxSizing: 'border-box' }} />
          </div>
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'none', cursor: 'pointer', fontSize: '0.875rem' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, background: '#4f46e5', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ ws, onDelete, onClose }) {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const match = confirm === ws.name;

  const handleDelete = async () => {
    setBusy(true);
    setError('');
    try {
      await onDelete(ws.id, ws.name);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4, color: '#ef4444' }}>Delete workspace</div>
        <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: 16 }}>
          This permanently deletes <strong>{ws.name}</strong> ({ws.owner_email}) and <strong>all</strong> of its data — employees, users, time entries, screenshots, teams, everything. This cannot be undone.
        </div>
        <label style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
          Type <strong>{ws.name}</strong> to confirm
        </label>
        <input
          autoFocus
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          placeholder={ws.name}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem', boxSizing: 'border-box', marginBottom: 12 }}
        />
        {error && <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'none', cursor: 'pointer', fontSize: '0.875rem' }}>Cancel</button>
          <button onClick={handleDelete} disabled={!match || busy} style={{ padding: '8px 18px', borderRadius: 8, background: match ? '#ef4444' : '#fca5a5', color: '#fff', border: 'none', cursor: match ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '0.875rem' }}>
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

function statusBadge(status, plan) {
  const colors = { active: '#16a34a', trial: '#d97706', cancelled: '#ef4444', past_due: '#dc2626' };
  const color = colors[status] || 'var(--muted)';
  const bg = { active: '#f0fdf4', trial: '#fffbeb', cancelled: '#fef2f2', past_due: '#fef2f2' };
  return (
    <span style={{ background: bg[status] || 'var(--hairline)', color, padding: '2px 8px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
      {plan !== 'trial' ? plan : status}
    </span>
  );
}

export default function OwnerAdmin({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('workspaces'); // 'workspaces' | 'integrations'

  const headers = { 'x-admin-token': getToken() };
  const authExpired = () => { localStorage.removeItem('hrtrack_admin_token'); onLogout(); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, w] = await Promise.all([
        axios.get(`${API}/api/owner/stats`, { headers }),
        axios.get(`${API}/api/owner/workspaces`, { headers }),
      ]);
      setStats(s.data);
      setWorkspaces(w.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('hrtrack_admin_token'); onLogout(); }
      else setError('Failed to load data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (id, data) => {
    await axios.put(`${API}/api/owner/workspaces/${id}`, data, { headers });
    await load();
  };

  const handleDelete = async (id, name) => {
    await axios.delete(`${API}/api/owner/workspaces/${id}`, { headers, data: { confirm: name } });
    await load();
  };

  const filtered = workspaces.filter(w =>
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    (w.owner_email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--hairline)', fontFamily: 'sans-serif' }}>
      {/* Header */}
      <div style={{ background: 'transparent', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>HR<span style={{ color: '#818cf8' }}>Track</span> <span style={{ color: '#818cf8', fontWeight: 400, fontSize: '0.85rem' }}>Owner Admin</span></div>
        <button onClick={onLogout} style={{ background: 'none', border: '1px solid rgba(255,255,255,.3)', color: '#fff', padding: '5px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem' }}>Logout</button>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[['workspaces', 'Workspaces'], ['integrations', 'Integrations']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setView(key)}
              style={{
                padding: '8px 18px', borderRadius: 10, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                border: '1px solid var(--glass-border)',
                background: view === key ? 'var(--primary, #4f46e5)' : 'var(--glass-2)',
                color: view === key ? '#fff' : 'var(--text)',
              }}
            >{label}</button>
          ))}
        </div>

        {view === 'integrations' && <OwnerIntegrations onAuthExpired={authExpired} />}

        {view === 'workspaces' && <>
        {/* Stats */}
        {stats && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
            <StatCard label="Total Workspaces" value={stats.total} />
            <StatCard label="Active (Paid)" value={stats.active_paid} color="#16a34a" />
            <StatCard label="On Trial" value={stats.on_trial} color="#d97706" />
            <StatCard label="Cancelled" value={stats.cancelled} color="#ef4444" />
          </div>
        )}

        {/* Table */}
        <div style={{ background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>All Workspaces</div>
            <input
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '7px 12px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem', width: 240 }}
            />
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--hairline)' }}>
                  {['Workspace', 'Owner Email', 'Plan / Status', 'Seats', 'Trial Left', 'Created', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-2)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>No workspaces found</td></tr>
                )}
                {filtered.map(ws => (
                  <tr key={ws.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{ws.name}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--muted)' }}>{ws.owner_email}</td>
                    <td style={{ padding: '12px 16px' }}>{statusBadge(ws.subscription_status, ws.plan)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {ws.seats_used} / {ws.seats_limit === null ? '∞' : ws.seats_limit}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {ws.trial_days_left === null ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Permanent</span>
                        : ws.trial_days_left === 0 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>Expired</span>
                        : <span style={{ color: ws.trial_days_left <= 3 ? '#ef4444' : '#d97706', fontWeight: 600 }}>{ws.trial_days_left}d</span>}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--muted)', fontSize: '0.8rem' }}>
                      {ws.created_at ? new Date(ws.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setEditing(ws)} style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid var(--glass-border)', background: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-2)' }}>
                          Manage
                        </button>
                        <button onClick={() => setDeleting(ws)} title="Delete workspace" style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #fecaca', background: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, color: '#ef4444' }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>}
      </div>

      {editing && <EditModal ws={editing} onSave={handleSave} onClose={() => setEditing(null)} />}
      {deleting && <DeleteModal ws={deleting} onDelete={handleDelete} onClose={() => setDeleting(null)} />}
    </div>
  );
}
